import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import speciesName from '../utils/speciesName';
import wantsCol from '../utils/wantsCol';
// MycoBank/MycoDB have no GBIF-key-based lookup (unlike INPN/Species
// Fungorum, both looked up server-side in SpeciesLookupService) - both
// build straight from the name, and both only make sense for an actual
// "Genus species" binomial, not a genus/family/order/... record or an
// infraspecific one (subsp./var./f.) - checked via rank (translated
// server-side, see SpeciesCorrectionLookup::RANK_LABELS) rather than
// GBIF's own 'SPECIES', and safe to assume canonicalName is a clean
// two-word name whenever that check passes, since Species::insertRankMarker()
// only ever splices a rank marker into it for those excluded infraspecific
// ranks.
//
// Genus comes from splitting `canonicalName` itself, not from the API's
// own `genus` classification field - confirmed the hard way that the two
// disagree for a synonym in a different genus than its accepted species
// (e.g. gbif key 7864731 "Boletus granulatus", a synonym of "Suillus
// granulatus": GBIF's own `genus` field on that record already reads
// "Suillus", its *current* classification, not "Boletus", the genus its
// own name is actually under - MycoBank/MycoDB need the latter). Both
// functions take a plain canonicalName string rather than the raw API
// species object, since speciesReferencesBox() below needs to build a
// MycoBank link from the *viewed* species' own name (synonym or not) but
// a MycoDB one from the *accepted* species' name regardless - two
// different names off the same API response, not always `s.canonicalName`.
function speciesNameParts(canonicalName) {
  const parts = canonicalName.trim().split(/\s+/);
  return {
    genus: parts[0],
    epithet: parts[parts.length - 1]
  };
}
function mycoBankUrl(canonicalName) {
  const {
    genus,
    epithet
  } = speciesNameParts(canonicalName);
  return 'http://mycobank.org/name/' + encodeURIComponent(genus + ' ' + epithet);
}
function mycoDbUrl(canonicalName) {
  const {
    genus,
    epithet
  } = speciesNameParts(canonicalName);
  return 'https://www.mycodb.fr/fiche.php?genre=' + encodeURIComponent(genus) + '&espece=' + encodeURIComponent(epithet);
}

// Both catalog fungi specifically - not the site's other GBIF-tracked
// kingdoms shown alongside them in the stats page's own species-by-group
// breakdown (Myxomycètes, Plantes, Insectes), which MycoBank/MycoDB simply
// don't cover. GBIF's own kingdom name, not translated server-side like
// rank is (see SpeciesCorrectionLookup::RANK_LABELS, which only touches
// rank). canonicalName needs at least two words (genus + epithet) for
// speciesNameParts() above to mean anything - excludes a genus/family/...
// record the same way the rank check does, and (defensively) any name
// missing its own genus word for some other reason.
function isFungalSpecies(_ref) {
  let {
    rank,
    kingdom,
    canonicalName
  } = _ref;
  return rank === 'Espèce' && kingdom === 'Fungi' && Boolean(canonicalName) && canonicalName.trim().split(/\s+/).length >= 2;
}

// Règne -> Genre, in GBIF's own field-naming order - each level (when
// present) links to that taxon's own /species/:id page via its GBIF key
// (colIdField, the *ColId sibling, only ever set in COL mode - see
// ColSpeciesOverlay's own docblock - links to /taxon/:id instead when the
// GBIF key itself isn't available).
const CLASSIFICATION_LEVELS = [['kingdom', 'kingdomKey', 'kingdomColId'], ['phylum', 'phylumKey', 'phylumColId'], ['class', 'classKey', 'classColId'], ['order', 'orderKey', 'orderColId'], ['family', 'familyKey', 'familyColId'], ['genus', 'genusKey', 'genusColId']];

/**
 * Shared building blocks for anything that renders a full GBIF species
 * record (`GET /api/species/:id?includeReferences=true`) the way
 * champis.net's own species/description pages do - used by both
 * SpeciesPage.js and DescriptionPage.js so the two stay visually and
 * behaviorally identical without duplicating this markup.
 */

/**
 * "ESPÈCE | VALIDE" / "GENRE | VALIDE" line, title, valid-taxon link and
 * basionym - everything above the fold on champis.net's own species pages.
 *
 * SpeciesLookupService::getSpeciesByIdCf() fills `s`'s *main* fields
 * (canonicalName/rank/status/...) with the *accepted* species whenever the
 * id it was given is a synonym, and stashes that synonym's own identity in
 * the synonym* fields instead - correct for a label appearing elsewhere
 * (SpeciesLabel's "Accepted name (= Synonym name)"), but visiting that
 * synonym's own /species/:id page needs the opposite emphasis: its own
 * name as the title, and a link *to* the accepted species (champis.net's
 * "Taxon valide : <name>") rather than a bare, unclickable synonym name
 * hanging off the accepted one. `s.synonymScientificName` being present at
 * all is exactly the signal that the requested id was such a synonym.
 *
 * `linkTitle` makes the title itself a link to that same name's own
 * /species/:id page (synonymId in the synonym-view case, s.id otherwise) -
 * off by default since SpeciesPage.js renders this header *on* that page
 * already (a self-link), but DescriptionPage.js passes it on: its title is
 * otherwise the only place on /description/:id with no way back to the
 * species page proper.
 *
 * `synonymSuffix` swaps the synonym-view treatment above for the opposite
 * emphasis DescriptionPage.js wants: the *accepted* name stays the big
 * title throughout (never swapped for the synonym's), with the synonym
 * appended right after it as a small "(= Synonym name)" suffix instead of
 * a separate "Taxon valide : ..." paragraph - the exact same treatment
 * DescriptionsPage.js/RandomDescriptions.js already use next to their own
 * (smaller) name, for one consistent look across every "description" of a
 * species rather than the found-under-a-synonym emphasis a dedicated
 * /species/:id page for that synonym wants instead.
 */
export function speciesHeader(s, _temp) {
  let {
    linkTitle = false,
    synonymSuffix = false
  } = _temp === void 0 ? {} : _temp;
  const isSynonymView = Boolean(s.synonymScientificName) && !synonymSuffix;
  const primaryName = isSynonymView ? {
    canonicalName: s.synonymCanonicalName,
    author: s.synonymAuthor,
    scientificName: s.synonymScientificName
  } : s;

  // status/rank are the literal string "UNDEFINED" for the "Species
  // incognita" pseudo-species (gbifId 0, see SpeciesLookupService) - not
  // real GBIF badges, so worth hiding rather than showing "UNDEFINED |
  // UNDEFINED". Irrelevant to the synonym-view branch, which always has a
  // real rank/status of its own.
  //
  // iucnCategoryLabel is appended to *either* branch, not swapped for a
  // synonym-specific one the way rank/status are - IUCN assesses the
  // underlying taxonomic concept, not the individual name combination on
  // screen, so it's the same badge regardless of which name this page is
  // showing (see SpeciesLookupService::addExternalReferences()'s own
  // docblock). Only present at all once includeReferences=true has been
  // fetched, and only for a species IUCN has actually assessed (that
  // method already excludes "not evaluated" - the common case for fungi).
  const badges = [...(isSynonymView ? [s.synonymRank, s.synonymStatus] : s.status === 'UNDEFINED' ? [] : [s.rank, s.status]), s.iucnCategoryLabel].filter(Boolean);
  const titleText = m('[', null, speciesName(primaryName), synonymSuffix && s.synonymCanonicalName && m("small", null, " (= ", speciesName({
    canonicalName: s.synonymCanonicalName,
    author: s.synonymAuthor,
    scientificName: s.synonymCanonicalName
  }), ")"));
  const titleId = isSynonymView ? s.synonymId : s.id;
  // s.colId is the colId of whatever gbif_id *this page's own identity* is
  // keyed by (see ColSpeciesBuilder::build()'s own docblock: it's set to
  // whichever raw colId was actually built from, i.e. the *synonym's* own
  // when this species itself is a ChecklistBank synonym) - correct only
  // when primaryName is that same synonym (isSynonymView true). When
  // primaryName is the *accepted* concept instead (either no synonym is
  // involved at all, or synonymSuffix suppressed the swap - DescriptionPage.tsx's
  // own call, synonymSuffix always true there), the accepted concept's own
  // colId is s.synonymAcceptedColId, not s.colId - using s.colId
  // unconditionally here was the actual bug (confirmed live 2026-09-21 on
  // a discussion tagged under a synonym name in COL mode: the title read
  // the *accepted* species' own name but linked to /taxon/:synonymColId,
  // its own synonym's id, not a mismatch a visitor could ever notice by
  // the URL alone, but not the accepted page it should have been), only
  // ever falling back to plain /species/:gbifId for every *other* case,
  // never actually reaching the accepted concept's real COL page.
  const titleColId = isSynonymView ? s.colId : s.synonymAcceptedColId ?? s.colId;
  const titleHref = titleColId && wantsCol() ? app.route.taxon(titleColId, primaryName.canonicalName) : app.route.species(titleId, primaryName.canonicalName);
  return m("div", {
    className: "SpeciesPage-header"
  }, badges.length > 0 && m("p", {
    className: "SpeciesPage-badges"
  }, badges.map(b => b.toUpperCase()).join(' | ')), m("h1", null, linkTitle ? m(Link, {
    href: titleHref
  }, titleText) : titleText), s.vernacularName && m("p", {
    className: "SpeciesPage-vernacular"
  }, s.vernacularName), isSynonymView && m("p", {
    className: "SpeciesPage-synonymOf"
  }, app.translator.trans('champis-species.forum.species_page.valid_taxon'), ' ', m(Link, {
    href: s.synonymAcceptedColId ? app.route.taxon(s.synonymAcceptedColId, s.canonicalName) : app.route.species(s.id, s.canonicalName)
  }, speciesName(s))), s.basionym && m("p", {
    className: "SpeciesPage-basionym"
  }, app.translator.trans('champis-species.forum.species_page.basionym'), ' ', s.basionymId ? m(Link, {
    href: app.route('species.legacy', {
      id: s.basionymId
    })
  }, s.basionym) : s.basionym));
}
// The external pages listed for a species, in display order. Also read by
// champis-exhibitions' CartothequeEntryEditModal (through flarum.reg), which
// shows them on one line rather than as this page's box.
export function speciesReferenceLinks(s) {
  // No real external page to link to for the "Species incognita"
  // pseudo-species (gbifId 0) - gbif.org/species/0 isn't a real GBIF
  // record, it's champis.net's own sentinel id.
  if (s.status === 'UNDEFINED') return [];

  // `s`'s own id/canonicalName/rank/genus/... are always the *accepted*
  // species' (see SpeciesLookupService) - same swap speciesHeader() makes
  // for its own primaryName. GBIF (or Catalogue of Life, see below)/INPN/
  // Species Fungorum/MycoBank should point at whichever name is actually on
  // screen (the synonym, when that's what this page was requested for);
  // `inpnUrl`/`speciesFungorumUrl` already come back from the API resolved
  // for that same id (see SpeciesLookupService::addExternalReferences()/
  // ColExternalReferencesService), no swap needed here.
  //
  // MycoDB is the deliberate exception: it always links to the *accepted*
  // species regardless (kept as plain `s` below) - unlike the others, it
  // has no per-synonym record of its own worth distinguishing, and
  // champis.net's own convention here is to send visitors straight to the
  // name MycoDB actually indexes fungi under.
  const isSynonymView = Boolean(s.synonymId);
  const current = isSynonymView ? {
    id: s.synonymId,
    canonicalName: s.synonymCanonicalName,
    scientificName: s.synonymScientificName,
    rank: s.synonymRank,
    kingdom: s.kingdom
  } : s;
  const links = [s.colOverlayApplied ? {
    name: 'Catalogue of Life',
    label: current.scientificName,
    url: 'https://www.catalogueoflife.org/data/taxon/' + s.colId
  } : {
    name: 'GBIF',
    label: current.scientificName,
    url: 'https://www.gbif.org/species/' + current.id
  }];
  if (s.inpnUrl) links.push({
    name: 'INPN',
    label: s.inpnLabel,
    url: s.inpnUrl
  });
  if (s.speciesFungorumUrl) links.push({
    name: 'Species Fungorum',
    label: s.speciesFungorumLabel,
    url: s.speciesFungorumUrl
  });
  // COL mode's own counterpart of the iucnCategoryLabel badge in
  // speciesHeader() above - ChecklistBank only ever gives a plain link to the
  // assessment, never the threat category itself (see
  // ColExternalReferencesService's own docblock), so this is a reference row
  // here rather than a badge.
  if (s.iucnUrl) links.push({
    name: 'IUCN',
    label: s.canonicalName,
    url: s.iucnUrl
  });
  if (isFungalSpecies(current)) {
    links.push({
      name: 'MycoBank',
      label: current.canonicalName,
      url: mycoBankUrl(current.canonicalName)
    });
  }
  if (isFungalSpecies(s)) links.push({
    name: 'MycoDB',
    label: s.canonicalName,
    url: mycoDbUrl(s.canonicalName)
  });
  return links;
}

// Each <li>'s whole content - label prefix included - is wrapped by one
// .SpeciesPage-referenceLink (an <a> here, a <Link> in
// speciesClassificationBox below) so the whole row is clickable, with a
// separator line between rows and no underline, matching the "boxed list"
// look of the page's other sections (Descriptions/Clés/Synonymes) - see
// .SpeciesPage-referenceLink in forum.less.
export function speciesReferencesBox(s) {
  const links = speciesReferenceLinks(s);
  if (!links.length) return null;
  return m("div", {
    className: "SpeciesPage-references"
  }, m("h2", {
    className: "ChampisSpeciesSearch-label"
  }, app.translator.trans('champis-species.forum.species_page.references')), m("ul", null, links.map(link => m("li", null, m("a", {
    href: link.url,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "SpeciesPage-referenceLink"
  }, m("strong", null, link.name, ":"), " ", link.label)))));
}
export function speciesClassificationBox(s) {
  const levels = CLASSIFICATION_LEVELS.filter(_ref2 => {
    let [nameField] = _ref2;
    return s[nameField];
  });
  if (!levels.length) return null;
  return m("div", {
    className: "SpeciesPage-classification"
  }, m("h2", {
    className: "ChampisSpeciesSearch-label"
  }, app.translator.trans('champis-species.forum.species_page.systematics')), m("ul", null, levels.map(_ref3 => {
    let [nameField, keyField, colIdField] = _ref3;
    const label = app.translator.trans(`champis-species.forum.species_page.rank_${nameField}`);
    const gbifKey = s[keyField];
    const colId = s[colIdField];

    // Not every level has its own gbif key or colId (see the ternary
    // this replaced) - those stay plain text, no hover/pointer (see
    // --plain in forum.less), rather than a dead-end link. gbifKey
    // takes priority (GBIF mode always has it, COL mode never does -
    // see ColSpeciesOverlay's own docblock, the two are mutually
    // exclusive in practice).
    if (gbifKey) {
      return m("li", {
        key: nameField
      }, m(Link, {
        href: app.route.species(gbifKey, s.classificationSlugs?.[nameField] || s[nameField]),
        className: "SpeciesPage-referenceLink"
      }, m("strong", null, label, ":"), " ", s[nameField]));
    }
    if (colId) {
      return m("li", {
        key: nameField
      }, m(Link, {
        href: app.route.taxon(colId, s[nameField]),
        className: "SpeciesPage-referenceLink"
      }, m("strong", null, label, ":"), " ", s[nameField]));
    }
    return m("li", {
      key: nameField
    }, m("span", {
      className: "SpeciesPage-referenceLink SpeciesPage-referenceLink--plain"
    }, m("strong", null, label, ":"), " ", s[nameField]));
  })));
}
flarum.reg.add('champis-species', 'forum/components/SpeciesInfoBoxes', { speciesHeader: speciesHeader,speciesReferenceLinks: speciesReferenceLinks,speciesReferencesBox: speciesReferencesBox,speciesClassificationBox: speciesClassificationBox, });